import { useMemo } from 'react'
import { Organization } from '../../api/organizations'
import { getDocIdAsString } from '../../utils/documents'
import {
  getWorkerAndOrganizationAssociation,
  getActiveContractualSchedule,
} from '../../utils/users'
import { useWeeksSummaryQuery } from '../../queries/missions'
import { useWorkersQuery } from '../../queries/users'
import { PayrollFilters } from '../../store/filters'
import { UserSummaries } from '../../api/missions'
import { PayrollWorkerWithWeeksSummary } from './use-payroll-workers'
import { useWorkerCounters } from './use-worker-counters'

/**
 * Return the full weeks summary for the currently selected worker, enriched with
 * association data and worker counters (for tooltips and period details).
 * Keeps concerns separated from the list/status hook.
 */
export const useWorkerWeeksSummary = (
  currentOrganization: Organization,
  filters: PayrollFilters,
  currentWorkerId: string | undefined,
): PayrollWorkerWithWeeksSummary | undefined => {
  const workersQuery = useWorkersQuery({
    contractModel: ['twoParties' as any],
    service: filters?.service,
    'start<': filters?.end,
    'end>': filters?.start,
    user: currentWorkerId ? [currentWorkerId] : filters?.user,
  })

  const currentWorkerWeeksSummaryQuery = useWeeksSummaryQuery(
    {
      from: filters?.start as Date,
      to: filters?.end as Date,
      user: currentWorkerId ? [currentWorkerId] : undefined,
      contractModel: ['twoParties' as any],
    },
    { enabled: Boolean(currentWorkerId && filters?.start && filters?.end) },
  )

  const workerCountersQuery = useWorkerCounters(currentWorkerId, currentOrganization?._id)

  return useMemo((): PayrollWorkerWithWeeksSummary | undefined => {
    if (!currentWorkerId || !workersQuery.data || !currentWorkerWeeksSummaryQuery.data) {
      return undefined
    }

    const worker = workersQuery.data.find(w => getDocIdAsString(w) === currentWorkerId)
    if (!worker) return undefined

    const association = getWorkerAndOrganizationAssociation(worker, currentOrganization)
    const weeksSummaryForWorker = currentWorkerWeeksSummaryQuery.data.find(
      (summary: UserSummaries) => getDocIdAsString(summary.user) === getDocIdAsString(worker),
    )

    if (!weeksSummaryForWorker) return undefined

    return {
      ...worker,
      association,
      weeksSummary: weeksSummaryForWorker,
      workerCounters: workerCountersQuery.data ?? undefined,
      weeklyHoursWorked: association
        ? getActiveContractualSchedule(association)?.weeklyHoursWorked
        : undefined,
    }
  }, [
    currentWorkerId,
    workersQuery.data,
    currentWorkerWeeksSummaryQuery.data,
    currentOrganization,
    workerCountersQuery.data,
  ])
}
